import StringUtils from '@/utils/StringUtils.js';
import RcDomainObject from "../../../common/RcDomainObject";
import Company from "../../company/Company.js";

/**
 * The latest value recorded against an employee for one thing that is tracked
 * about them - when they last signed in, and whatever follows it.
 *
 * One row per employee per tracked thing, identified by its Key. The same shape
 * as VehicleData: the records of what happened stay where they are, and this is
 * the latest-value lookup that answers "when did this last happen" without
 * walking that history.
 */
export default class EmployeeData extends RcDomainObject {

  static MODEL_NAME = 'EmployeeData';

  static Fields = {
    Key: "key",
    Value: "value",
    RecordedOn: "recordedOn",
    // Spelled out rather than read off the model classes: these run in a static
    // initialiser, and Company imports back round to here, so reading
    // Company.MODEL_NAME lands on a half-built module and returns undefined.
    Employee: "Employee",
    Company: "Company",
  }

  static Values = {
    Keys: {
      // The last time this employee signed in to this company.
      LastSignIn: "lastSignIn",
    }
  }

  constructor(domain, data) {
    super(domain, data, EmployeeData.MODEL_NAME);
  }

  fields() {
    return EmployeeData.Fields;
  }

  find() {
    var result = this.domain.employees().employeeData().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    return result;
  }

  key() {
    return this.getString(EmployeeData.Fields.Key);
  }

  withKey(key) {
    this.put(EmployeeData.Fields.Key, key);
    return this;
  }

  isKey(key) {
    return StringUtils.equals(key, this.key());
  }

  isLastSignIn() {
    return this.isKey(EmployeeData.Values.Keys.LastSignIn);
  }

  withKeyLastSignIn() {
    return this.withKey(EmployeeData.Values.Keys.LastSignIn);
  }

  value() {
    return this.getString(EmployeeData.Fields.Value);
  }

  withValue(value) {
    this.put(EmployeeData.Fields.Value, StringUtils.toString(value));
    return this;
  }

  hasValue() {
    return StringUtils.isNotEmpty(this.value());
  }

  // The value as a number, or NaN when nothing usable is recorded. Callers
  // comparing values should check for NaN rather than trusting a 0.
  numberValue() {
    return parseFloat(this.value());
  }

  // A sign-in row holds epoch milliseconds, so this is how a caller reads one
  // back as a time without parsing the text itself. 0 when nothing is recorded.
  timeValue() {
    var value = parseInt(this.value());
    return isNaN(value) ? 0 : value;
  }

  recordedOn() {
    var value = parseInt(this.get(EmployeeData.Fields.RecordedOn));
    return isNaN(value) ? 0 : value;
  }

  withRecordedOn(recordedOn) {
    this.put(EmployeeData.Fields.RecordedOn, recordedOn);
    return this;
  }

  // The moment is both the value and the time it was recorded, so the guard
  // against an older write replacing a newer one works the same way here as it
  // does for a meter reading.
  withLastSignIn(signedInOn) {
    this.withKeyLastSignIn()
      .withValue(signedInOn)
      .withRecordedOn(signedInOn);
    return this;
  }

  isNewerThan(recordedOn) {
    return this.recordedOn() > recordedOn;
  }

  company() {
    var data = this.get(EmployeeData.Fields.Company);
    return new Company(this.domain, data);
  }

  withCompany(company) {
    this.put(EmployeeData.Fields.Company, company);
    return this;
  }

  copy() {
    var data = this._copy();
    return new EmployeeData(this.domain, data);
  }
}
